import { HubConnectionBuilder, HttpTransportType, LogLevel } from "@microsoft/signalr";
import { fromEventPattern, BehaviorSubject, fromEvent, combineLatest } from "rxjs";
import {
  switchMap,
  tap,
  filter,
  switchMapTo,
  retry,
  map,
  startWith,
  takeUntil,
  repeat
} from "rxjs/operators";
import { mapState } from "vuex";
import store from "@/store";
import { handleErrors } from "@/modules/handleErrors";

function fromWebsocketEvent(connection, event) {
  return fromEventPattern(
    handler => connection.on(event, handler),
    handler => connection.off(event, handler)
  );
}

const BASE_URL = process.env.VUE_APP_API_BASE_URL;
const connection = new HubConnectionBuilder()
  .withUrl(`${BASE_URL}/casehub`, {
    accessTokenFactory: () => {
      return store.state.token;
    },
    // Add headers configuration to support consistent HTTP parsing
    headers: {
      // Explicitly set Content-Type to help with consistent parsing
      "Content-Type": "application/json",
      // Don't include Transfer-Encoding header from the client
      "X-Requested-With": "XMLHttpRequest"
    },
    serverTimeoutInMilliseconds: 30000, // 30 seconds
    // Specify client timeout - how long client will wait after attempting to reconnect
    keepAliveIntervalInMilliseconds: 15000, // 15 seconds
    // Add transport fallback options
    transport: HttpTransportType.WebSockets,
    skipNegotiation: true
  })
  .withAutomaticReconnect([0, 2000, 10000, 30000]) // More sophisticated reconnect strategy
  .configureLogging(LogLevel.Warning) // Add logging
  .build();
const wsConnectionStatus$ = new BehaviorSubject({ connected: false });
connection.onclose(() => {
  console.log("Connection closed 🚫");
  wsConnectionStatus$.next({ connected: false });
});
connection.onreconnected(() => wsConnectionStatus$.next({ connected: true }));
connection.onreconnecting(() => wsConnectionStatus$.next({ connected: false }));
const relativeTimeFormmater = new Intl.RelativeTimeFormat("en", {
  style: "short",
  numeric: "auto"
});
const dateFormatter = new Intl.DateTimeFormat("en", {
  dateStyle: "full",
  timeStyle: "short"
});

const documentVisibility$ = fromEvent(document, "visibilitychange").pipe(
  map(() => document.visibilityState === "visible"),
  startWith(true)
);

const startConnectionSafely = async () => {
  try {
    // Only start if disconnected
    if (connection.state === "Disconnected") {
      await connection.start();
      console.log("Connection started successfully");
      wsConnectionStatus$.next({ connected: true });
      return true;
    } else if (connection.state === "Connected") {
      console.log("Connection already established");
      wsConnectionStatus$.next({ connected: true });
      return true;
    } else {
      console.log("Connection in progress, state:", connection.state);
      return false;
    }
  } catch (error) {
    console.error("Failed to start connection:", error);
    wsConnectionStatus$.next({ connected: false });
    return false;
  }
};

export default {
  data() {
    return {
      enterTime: new Date().getTime()
    };
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser,
      token: state => state.token
    }),
    isEnabled() {
      const stage = process.env.VUE_APP_STAGE;
      const environment = process.env.NODE_ENV || "development";
      if (stage === "QA" || stage === "QA2") {
        return false; // Disable in QA environments
      }
      if (environment === "development" || environment === "test") {
        return false; // Disable in development and test environments
      }
      return true; // Enable in production, uat, staging
    },
    displayName() {
      return this.currentUser.displayName;
    },
    groupName() {
      const caseId = this.$route.params.caseId;
      if (caseId) {
        return caseId.toString();
      }
      return null;
    },
    pageName() {
      return this.$route.name;
    }
  },
  subscriptions() {
    const closeConnection$ = documentVisibility$.pipe(
      filter(isVisible => !isVisible),
      tap(() => console.log("closing connection")),
      switchMap(() => connection.stop())
    );

    const startConnection$ = this.$watchAsObservable("token", { immediate: true }).pipe(
      filter(({ newValue }) => !!newValue && this.isEnabled),
      switchMapTo(combineLatest([wsConnectionStatus$, documentVisibility$])),
      filter(([{ connected }, isVisible]) => !connected && isVisible),
      switchMap(async () => {
        try {
          await startConnectionSafely();
        } catch (error) {
          handleErrors(error);
        }
        return wsConnectionStatus$.next({ connected: true });
      }),
      retry(5)
    );

    const joinCase$ = wsConnectionStatus$.pipe(
      // Only subscribe if the connection is open
      filter(({ connected }) => connected),
      tap(() => console.log("Connected to CaseHub 🚀")),
      switchMap(() => this.$watchAsObservable("groupName", { immediate: true })),
      filter(({ newValue, oldValue }) => {
        if (newValue != oldValue && newValue) {
          return true;
        }
        return false;
      }),
      switchMap(async ({ newValue }) => {
        return this.joinCase(newValue);
      })
    );
    const leaveCase$ = wsConnectionStatus$.pipe(
      filter(({ connected }) => connected),
      switchMap(() => this.$watchAsObservable("groupName")),
      filter(({ newValue, oldValue }) => {
        if (newValue != oldValue && oldValue) {
          return true;
        }
        return false;
      }),
      switchMap(async ({ oldValue }) => {
        return this.leaveCase(oldValue);
      })
    );
    const receiveMessage$ = wsConnectionStatus$.pipe(
      filter(({ connected }) => connected),
      switchMapTo(fromWebsocketEvent(connection, "ReceiveMessage")),
      tap(() => console.log("Regular message received.")),
      filter(data => {
        if (Array.isArray(data)) {
          const [user] = data;
          return this.groupName && user !== this.displayName;
        }
        return false;
      }),
      tap(data => {
        if (Array.isArray(data)) {
          const [user, message] = data;
          console.log("Message from %s\n%s", user, data);
          window.alert(message);
        }
      }),
      takeUntil(combineLatest([leaveCase$, closeConnection$])),
      repeat()
    );

    const joinMessage$ = wsConnectionStatus$.pipe(
      filter(({ connected }) => connected),
      switchMapTo(fromWebsocketEvent(connection, "UserJoin")),
      tap(() => console.log("Join message received.")),
      filter(user => this.groupName && user !== this.displayName),
      tap(user => {
        const enterDate = this.getEnterDate();
        window.notify(`${user} has opened this case.`, "info", 5000);
        connection.invoke(
          "SendMessageToGroup",
          this.groupName,
          "User",
          `This case was opened by ${this.displayName} on ${enterDate}.  Proceeding with any edits may result in data being lost because two users are editing this case at same time. We recommend you exit case and return later.`
        );
      }),
      takeUntil(combineLatest([leaveCase$, closeConnection$])),
      repeat()
    );

    return {
      wsConnectionStatus$,
      joinCase$,
      leaveCase$,
      startConnection$,
      receiveMessage$,
      joinMessage$,
      closeConnection$
    };
  },
  methods: {
    async joinCase(caseId) {
      if (caseId) {
        if (!this.getConnectionStatus()) {
          return;
        }
        this.enterTime = new Date().getTime();
        return connection.invoke("JoinGroup", caseId, this.currentUser.displayName);
      }
    },
    getConnectionStatus() {
      return this.wsConnectionStatus$.connected;
    },
    getEnterTime() {
      const timeinms = new Date().getTime() - this.enterTime;
      const seconds = Math.floor(timeinms / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const unit = minutes > 1 ? (hours > 1 ? "hours" : "minutes") : "seconds";
      const value = minutes > 1 ? (hours > 1 ? hours : minutes) : seconds;
      return relativeTimeFormmater.format(0 - value, unit);
    },

    getEnterDate() {
      const enterDate = new Date(this.enterTime);
      return dateFormatter.format(enterDate);
    },
    async leaveCase(caseId) {
      try {
        await connection.invoke("LeaveGroup", caseId);
      } catch (error) {
        console.error("Error leaving case:", error);
      }
    },
    handleBeforeUnload() {
      connection.stop();
    }
  },
  beforeDestroy() {
    this.handleBeforeUnload();
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  created() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }
};
